import { Button } from '@codegouvfr/react-dsfr/Button';
import { ORGANISME_FORME_JURIDIQUE_TEXTS, useTextes } from '@common/config';
import { EditOfaInfoStep1FormResult, editOfaInfoStep1FormSchema } from '@common/schemas/forms/organisme';
import { OrganismeDetailsDto } from '@common/services';
import { DangerButton, Form, FormField, SelectField } from '@common/ui';
import { areSomePropertiesDifferent } from "@common/utils";
import React, { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ZodObject } from 'zod';

const COMPARED_FIELDS: (keyof EditOfaInfoStep1FormResult)[] = [
  'denominationUsuelle', 'formeJuridique', 'adresse', 'complementAdresse', 'codePostal', 'ville',
];

export const EditOfaInfoStep1 = ({ organisme, organismePrecedent, handleSubmit }: EditOfaInfoStep1Props) => {

  const t = useTextes('ParcoursOfa', 'EtapierStep1');
  const navigate = useNavigate();

  const formSchemaRef = useRef<ZodObject>(editOfaInfoStep1FormSchema);

  const resolveField = (current: string | null | undefined, previous: string | null | undefined): string =>
    current?.trim() || previous?.trim() || '';

  const values = useMemo<EditOfaInfoStep1FormResult>(() => ({
    denominationUsuelle: resolveField(organisme.denominationUsuelle, organismePrecedent?.denominationUsuelle),
    formeJuridique: resolveField(organisme.formeJuridique, organismePrecedent?.formeJuridique),
    adresse: resolveField(organisme.adresse, organismePrecedent?.adresse),
    complementAdresse: resolveField(organisme.complementAdresse, organismePrecedent?.complementAdresse),
    codePostal: resolveField(organisme.codePostal, organismePrecedent?.codePostal),
    ville: resolveField(organisme.ville, organismePrecedent?.ville),
  }), [organisme, organismePrecedent]);

  const handleSubmitInfoStep1 = (result: EditOfaInfoStep1FormResult) => {
    const hasChanged = areSomePropertiesDifferent(values, result, COMPARED_FIELDS);
    handleSubmit(result, hasChanged);
  };

  return (
    <div className={'flex flex-col p-6'}>
      <Form<EditOfaInfoStep1FormResult> schema={formSchemaRef.current}
                                        defaultValues={values}
                                        className={'flex flex-col gap-6'}
                                        onSubmit={handleSubmitInfoStep1}>

        <FormField name="denominationUsuelle" title="Dénomination usuelle de l'organisme" />

        <SelectField name="formeJuridique"
                     title="Forme juridique de l'organisme"
                     options={Object.entries(ORGANISME_FORME_JURIDIQUE_TEXTS).map(([key, value]) => ({
                       value: key,
                       label: value,
                     }))} />

        <FormField name="adresse" title="Adresse du siège social de l'organisme"
                   placeholder={'12 Rue des Vaux parés'}
                   tooltip={t['DONNEE_OFA_COMP_ADRESSE_INFO_BULLE_MSG']}/>

        <FormField name="complementAdresse" title="Complément d'adresse" placeholder={'Bâtiment C'} />
        <FormField name="codePostal" title="Code postal " placeholder={'56788'} />

        <FormField name="ville" title="Ville " placeholder={'Vannes'} />

        <div className="separator" />

        <div className={'flex justify-end gap-2'}>
          <DangerButton type={'button'} onClick={() => navigate('/')}>ANNULER</DangerButton>
          <Button type={'submit'}>SUIVANT</Button>
        </div>
      </Form>
    </div>
  );
};

interface EditOfaInfoStep1Props {
  organisme: OrganismeDetailsDto,
  organismePrecedent: OrganismeDetailsDto | null;
  handleSubmit: (edit: EditOfaInfoStep1FormResult, hasChanged: boolean) => void
}